import {
  PSV_STATE_SCOPED_SOURCE_FAMILIES,
  PsvMonitoringStateScope,
  type PsvSourceFamily,
} from "@api/shared-runtime/psv-monitoring";
import { ALL_SOURCE_FAMILIES } from "./monitoring-constants";
import type {
  MonitoringProgram,
  SourceDraft,
  StateCode,
} from "./monitoring-types";

export function isStateScoped(family: PsvSourceFamily): boolean {
  return PSV_STATE_SCOPED_SOURCE_FAMILIES.includes(family);
}

/** State-scoped families start on "Only these states" with nothing chosen, so
 * the user has to pick states before the selection is valid. */
export function newSourceDraft(family: PsvSourceFamily): SourceDraft {
  if (isStateScoped(family)) {
    return {
      family,
      stateScope: PsvMonitoringStateScope.Selected,
      stateCodes: [],
    };
  }
  return { family, stateScope: null, stateCodes: [] };
}

export function allSourcesDraft(): SourceDraft[] {
  return ALL_SOURCE_FAMILIES.map(newSourceDraft);
}

export function findSource({
  sources,
  family,
}: {
  sources: readonly SourceDraft[];
  family: PsvSourceFamily;
}): SourceDraft | undefined {
  return sources.find((source) => source.family === family);
}

export function toggleSource({
  sources,
  family,
  selected,
}: {
  sources: readonly SourceDraft[];
  family: PsvSourceFamily;
  selected: boolean;
}): SourceDraft[] {
  const others = sources.filter((source) => source.family !== family);
  if (!selected) {
    return others;
  }
  return ALL_SOURCE_FAMILIES.flatMap((candidate) => {
    if (candidate === family) {
      return [newSourceDraft(family)];
    }
    return others.filter((source) => source.family === candidate);
  });
}

export function replaceSource({
  sources,
  next,
}: {
  sources: readonly SourceDraft[];
  next: SourceDraft;
}): SourceDraft[] {
  return sources.map((source) =>
    source.family === next.family ? next : source,
  );
}

export function withStateScope({
  source,
  stateScope,
}: {
  source: SourceDraft;
  stateScope: PsvMonitoringStateScope;
}): SourceDraft {
  if (stateScope === PsvMonitoringStateScope.All) {
    return { ...source, stateScope, stateCodes: [] };
  }
  return { ...source, stateScope };
}

export function withStateCodes({
  source,
  stateCodes,
}: {
  source: SourceDraft;
  stateCodes: string[];
}): SourceDraft {
  // The state select only offers valid codes.
  return { ...source, stateCodes: stateCodes as StateCode[] };
}

/** Selected sources set to "Only these states" with none chosen yet. */
export function familiesMissingStates(
  sources: readonly SourceDraft[],
): PsvSourceFamily[] {
  return sources
    .filter(
      (source) =>
        source.stateScope === PsvMonitoringStateScope.Selected &&
        source.stateCodes.length === 0,
    )
    .map((source) => source.family);
}

/** Saved rules arrive with readonly state codes; drafts are editable copies. */
export function toSourceDraft(
  source: MonitoringProgram["sources"][number],
): SourceDraft {
  return { ...source, stateCodes: [...source.stateCodes] };
}
